import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Link2,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Trash2,
} from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ActivityItemHeaderFragment } from '@graphql/__generated__/graphql';
import { useActivityContext } from '@modules/_shared/components/feed/ActivityProvider';
import { UPDATE_ACTIVITY_PINNED } from '@modules/_shared/components/feed/utils/activityQueries';
import { isProfileInquiryAssignment } from '@modules/_shared/components/feed/utils/activitySource';
import { ActivityStatusChip } from '@modules/_shared/components/feed-v2/components/ActivityStatusChip';
import { ActivityTitle } from '@modules/_shared/components/feed-v2/components/ActivityTitle';
import { AssignmentStatusChip } from '@modules/_shared/components/feed-v2/components/AssignmentStatusChip';
import { DateTooltipContent } from '@modules/_shared/components/feed-v2/components/DateTooltipContent';
import {
  baseChipSx,
  chipTypographyStyles,
} from '@modules/_shared/components/feed-v2/components/statusChipStyles';
import {
  type ActivityVisibility,
  VisibilityChip,
} from '@modules/_shared/components/feed-v2/components/VisibilityChip';
import {
  EDITABLE_TYPES,
  isEditableActivityType,
  isOutcomeActivityType,
} from '@modules/_shared/components/feed-v2/constants/activityConfig';
import { tooltipProps } from '@modules/_shared/components/feed-v2/constants/styles';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import { useLocale } from '@shared/hooks/locale';

export const ACTIVITY_ITEM_HEADER_FRAGMENT = gql(/* GraphQL */ `
  fragment ActivityItemHeader on activities {
    id
    activity_type
    created_by
    created_at
    due_at
    lead_id
    start_date
    pinned
    visibility
    creator {
      ...UserAvatar
    }
    assignee {
      ...UserAvatar
    }
    ...ActivityTitle
    ...AssignmentStatusChip
    ...DateTooltipContent
  }
`);

type Props = {
  activity: ActivityItemHeaderFragment;
  isIndirect: boolean;
  activitiesCount: number;
  done: boolean;
  onDelete: () => void;
};

export const ActivityItemHeader = ({
  activity,
  isIndirect,
  activitiesCount,
  done,
  onDelete,
}: Props) => {
  const { me } = useAppData();
  const { t } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const { openDialog } = useActivityContext();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const isAdmin = me?.is_admin ?? false;

  // Edit/Delete permissions
  const canEdit = EDITABLE_TYPES.includes(activity.activity_type);
  const canDelete =
    activity.activity_type !== 'module_granted' &&
    activity.activity_type !== 'module_revoked' &&
    activity.activity_type !== 'onboarding_status_changed' &&
    activity.activity_type !== 'agent_profile_visibility_changed' &&
    (isAdmin || activity.created_by === me?.id);

  const handleEdit = useCallback(() => {
    setMenuAnchor(null);
    if (canEdit && isEditableActivityType(activity.activity_type)) {
      openDialog(activity.activity_type, 'edit', activity.id);
    }
  }, [canEdit, openDialog, activity.activity_type, activity.id]);

  const handleDelete = useCallback(() => {
    setMenuAnchor(null);
    onDelete();
  }, [onDelete]);

  const [updatePinned] = useMutation(UPDATE_ACTIVITY_PINNED, {
    onError: setErrorLike,
  });

  const profileInquiryContact =
    isProfileInquiryAssignment(activity) &&
    activity.lead?.profile_contact != null &&
    userCanViewLeadDetails(me, activity.lead)
      ? {
          ...activity.lead.profile_contact,
          user_images: activity.lead.profile_contact.profile_image_url
            ? [
                {
                  image: {
                    url: activity.lead.profile_contact.profile_image_url,
                  },
                },
              ]
            : [],
        }
      : null;

  const handleTogglePin = useCallback(() => {
    setMenuAnchor(null);
    updatePinned({
      variables: { id: activity.id, pinned: !activity.pinned },
      optimisticResponse: {
        update_activities_by_pk: {
          id: activity.id,
          pinned: !activity.pinned,
        },
      },
      onCompleted: () => {
        emitFeedRefresh();
      },
    });
  }, [activity.id, activity.pinned, updatePinned]);

  return (
    <>
      {/* Main row */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0.5, sm: 1 }}
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'flex-start',
            flex: 1,
            minWidth: 0,
            pr: { xs: 4, sm: 0 },
          }}
        >
          <UserAvatar
            user={
              done && isOutcomeActivityType(activity.activity_type)
                ? activity.assignee ?? activity.creator
                : profileInquiryContact ?? activity.creator
            }
            size={20}
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              bgcolor: 'grey.400',
              flexShrink: 0,
              mt: '2px !important',
            }}
          />
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              flexWrap: 'wrap',
              rowGap: 0.5,
              columnGap: 1,
              minHeight: '24px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                wordBreak: 'break-word',
              }}
            >
              <ActivityTitle
                activity={activity}
                count={activitiesCount}
                allDone={done}
              />
            </Typography>
            {/* Assignment status indicator */}
            {activity.activity_type === 'assignment' && (
              <AssignmentStatusChip activity={activity} />
            )}
            {/* Other activity types - pending indicator */}
            {activity.activity_type !== 'assignment' && !done && (
              <ActivityStatusChip
                due_at={activity.due_at}
                start_date={activity.start_date}
              />
            )}
            {/* Visibility chip (admin only) */}
            {isAdmin && (
              <VisibilityChip
                visibility={activity.visibility as ActivityVisibility}
              />
            )}
            {/* Linked lead indicator */}
            {isIndirect && (
              <Tooltip
                title={t(
                  'This activity is linked to another lead by the same contact',
                )}
                {...tooltipProps}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: 'center',
                    ...baseChipSx,
                    bgcolor: 'grey.100',
                  }}
                >
                  <Link2 size={12} color={palette.grey[600]} />
                  <Typography
                    variant="caption"
                    sx={{ color: 'grey.600', ...chipTypographyStyles }}
                  >
                    {t('Linked')}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Desktop-only: Actions menu trigger + timestamp */}
        <Stack
          direction="row"
          spacing={{ xs: 3.5, sm: 0.5 }}
          sx={{
            alignItems: 'center',
          }}
        >
          {/* Pin indicator */}
          {activity.pinned && (
            <Tooltip title={t('Pinned')} {...tooltipProps}>
              <Pin
                size={14}
                color={palette.primary.main}
                style={{ flexShrink: 0 }}
              />
            </Tooltip>
          )}
          {/* On mobile, the 3-dots menu is moved to the top-right corner */}
          {(canEdit || canDelete) && (
            <IconButton
              aria-label={t('More options')}
              onClick={e => setMenuAnchor(e.currentTarget as HTMLElement)}
              size="small"
              sx={theme => ({
                borderRadius: '4px',
                p: '4px',
                color: 'text.secondary',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: 'grey.100',
                  color: 'text.primary',
                },
                [theme.breakpoints.down('sm')]: {
                  position: 'absolute',
                  top: 12,
                  right: 8,
                  zIndex: 1,
                },
              })}
            >
              <MoreHorizontal size={16} />
            </IconButton>
          )}
          <Tooltip
            title={<DateTooltipContent activity={activity} />}
            {...tooltipProps}
          >
            <Typography
              variant="caption"
              component="span"
              sx={{
                color: 'text.secondary',
                flexShrink: 0,
                cursor: 'default',
              }}
            >
              <TimeAgo dateString={activity.created_at} disableTooltip />
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
      {/* Shared Menu for both mobile and desktop triggers */}
      {(canEdit || canDelete) && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                minWidth: 120,
              },
            },
          }}
        >
          <MenuItem onClick={handleTogglePin} sx={{ py: 0.75, px: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {activity.pinned ? (
                <PinOff size={16} color={palette.grey[600]} />
              ) : (
                <Pin size={16} color={palette.grey[600]} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={activity.pinned ? t('Unpin') : t('Pin to top')}
              slotProps={{
                primary: {
                  variant: 'body2',
                  sx: { fontWeight: 500 },
                },
              }}
            />
          </MenuItem>
          {canEdit && (
            <MenuItem onClick={handleEdit} sx={{ py: 0.75, px: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Pencil size={16} color={palette.grey[600]} />
              </ListItemIcon>
              <ListItemText
                primary={t('Edit')}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    sx: { fontWeight: 500 },
                  },
                }}
              />
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              onClick={handleDelete}
              sx={{ py: 0.75, px: 1.5, color: 'error.main' }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Trash2 size={16} color="#ef4444" />
              </ListItemIcon>
              <ListItemText
                primary={t('Delete')}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    sx: { fontWeight: 500 },
                  },
                }}
              />
            </MenuItem>
          )}
        </Menu>
      )}
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
